import { formatearMoneda, type EstadoVenta, type MetodoPago, type VentaDetalle, type VentaLineaVista } from '@barbones/shared';
import { useId, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/cliente';
import { Insignia } from '@/components/ui/data';
import { fechaHora } from '@/lib/utils';

const ESTADO: Record<EstadoVenta, { texto: string; tono: 'exito' | 'peligro' | 'aviso' | 'neutro' }> = {
  pagada: { texto: 'Pagada', tono: 'exito' },
  cancelada: { texto: 'Cancelada', tono: 'peligro' },
  abierta: { texto: 'En servicio', tono: 'aviso' },
  por_cobrar: { texto: 'Por cobrar', tono: 'aviso' },
  suspendida: { texto: 'Suspendida', tono: 'neutro' },
};
const METODO: Record<MetodoPago, string> = { efectivo: 'Efectivo', transferencia: 'Transferencia', tarjeta: 'Tarjeta' };
const DEVOLUCION = { aplicada: { texto: 'Aplicada', tono: 'neutro' }, pendiente: { texto: 'Por autorizar', tono: 'aviso' } } as const;

/** Carrito de una venta (solo lectura). Consulta solo con un id; quien llama revisa `ventas.ver_detalle`. */
export const useVentaDetalle = (ventaId: string | null | undefined) =>
  useQuery({ queryKey: ['ventas', 'detalle', ventaId], queryFn: () => api<VentaDetalle>(`/ventas-detalle/${ventaId}`), enabled: !!ventaId });

/** Bloque con título visible; el título también nombra la región para lectores de pantalla. */
function Seccion({ titulo, children }: { titulo: string; children: ReactNode }) {
  const id = useId();
  return (
    <section aria-labelledby={id} className="flex flex-col gap-1.5">
      <h3 id={id} className="text-[12px] font-medium uppercase tracking-wide text-foreground-muted">
        {titulo}
      </h3>
      {children}
    </section>
  );
}

/** Renglón concepto → importe, con detalle opcional en gris debajo del importe. */
function Renglon({ concepto, importe, detalle }: { concepto: ReactNode; importe: ReactNode; detalle?: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span>{concepto}</span>
      <span className="cifras flex flex-col items-end text-right">
        <span>{importe}</span>
        {detalle}
      </span>
    </div>
  );
}

/**
 * Detalle de solo lectura de una venta, en el orden en que se revisa: quién participó, qué compró,
 * cómo pagó, la propina y las devoluciones.
 * `accionLinea` agrega una acción bajo una línea (p. ej. «Cambiar servicio» durante la atención).
 */
export function DetalleVenta({ venta, accionLinea }: { venta: VentaDetalle; accionLinea?: (linea: VentaLineaVista) => ReactNode }) {
  const estado = ESTADO[venta.estado];
  const fecha = venta.pagadaEn ?? venta.canceladaEn ?? venta.creadaEn;
  const conDevolucion = venta.devoluciones.some((d) => d.estado === 'aplicada');
  // Una venta de mostrador no guarda barbero: se toma de quienes hicieron los servicios.
  const barberos = venta.barbero
    ? [venta.barbero]
    : [...new Map(venta.lineas.filter((l) => l.tipo === 'servicio' && l.comisionista).map((l) => [l.comisionista!.id, l.comisionista!])).values()];
  const barberoUnico = barberos.length === 1 ? barberos[0]!.id : null;
  return (
    <section aria-label={`Venta folio ${venta.folio}`} className="flex flex-col gap-4 text-[13px]">
      <div className="flex flex-col gap-1">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-[15px] font-medium">Folio {venta.folio}</p>
          <span className="flex items-center gap-1.5">
            <Insignia tono={estado.tono}>{estado.texto}</Insignia>
            {conDevolucion && <Insignia tono="aviso">Con devolución</Insignia>}
          </span>
        </div>
        <p className="flex flex-wrap justify-between gap-x-3 text-foreground-muted">
          <span>{venta.cliente?.nombre ?? 'Mostrador'}</span>
          <span className="cifras">{fechaHora(fecha)}</span>
        </p>
      </div>
      {venta.motivoCancelacion && <p className="rounded-md bg-muted px-3 py-2">Motivo: {venta.motivoCancelacion}</p>}

      <Seccion titulo="Quién participó">
        <dl className="grid grid-cols-2 gap-2 rounded-md border border-border px-3 py-2.5">
          <div>
            <dt className="text-[12px] text-foreground-muted">Barbero</dt>
            <dd className="text-[14px] font-medium">{barberos.map((b) => b.nombre).join(', ') || '—'}</dd>
          </div>
          <div>
            <dt className="text-[12px] text-foreground-muted">Cobró en caja</dt>
            <dd className="text-[14px] font-medium">{venta.cobradaPor ?? '—'}</dd>
          </div>
        </dl>
      </Seccion>

      <Seccion titulo="Qué compró">
        <ul className="flex flex-col divide-y divide-border rounded-md border border-border">
          {venta.lineas.map((l) => (
            <li key={l.id} className="flex items-start justify-between gap-3 px-3 py-2">
              <div className="min-w-0">
                <p>{l.cantidad > 1 ? `${l.cantidad}× ${l.descripcion}` : l.descripcion}</p>
                <p className="text-[12px] text-foreground-muted">
                  {l.tipo === 'servicio' ? 'Servicio' : 'Producto'} · {formatearMoneda(l.precioUnitario)} c/u
                  {l.comisionista && l.comisionista.id !== barberoUnico ? ` · atendió ${l.comisionista.nombre}` : ''}
                  {l.descuento > 0 ? ` · descuento ${formatearMoneda(l.descuento)}` : ''}
                  {l.cantidadDevuelta > 0 ? ` · ${l.cantidadDevuelta} devuelto(s)` : ''}
                </p>
                {accionLinea?.(l)}
              </div>
              <span className="cifras shrink-0">{formatearMoneda(l.neto)}</span>
            </li>
          ))}
        </ul>
        <dl className="cifras grid grid-cols-[1fr_auto] gap-x-4 gap-y-1 px-1">
          {(venta.descuento > 0 || venta.ajusteCorreccion !== 0) && (
            <>
              <dt className="text-foreground-muted">Subtotal</dt>
              <dd className="text-right">{formatearMoneda(venta.subtotal)}</dd>
            </>
          )}
          {venta.descuento > 0 && (
            <>
              <dt className="text-foreground-muted">Descuento</dt>
              <dd className="text-right">−{formatearMoneda(venta.descuento)}</dd>
            </>
          )}
          {venta.ajusteCorreccion !== 0 && (
            <>
              <dt className="text-foreground-muted">Corrección</dt>
              <dd className="text-right">{venta.ajusteCorreccion < 0 ? `−${formatearMoneda(-venta.ajusteCorreccion)}` : `+${formatearMoneda(venta.ajusteCorreccion)}`}</dd>
            </>
          )}
          <dt className="text-[14px] font-semibold">Total de la venta</dt>
          <dd className="text-right text-[14px] font-semibold">{formatearMoneda(venta.total)}</dd>
        </dl>
      </Seccion>

      {venta.pagos.length > 0 && (
        <Seccion titulo="Cómo pagó">
          {venta.pagos.map((p) => (
            <Renglon
              key={p.id}
              concepto={METODO[p.metodo as MetodoPago] ?? p.metodo}
              importe={p.metodo === 'efectivo' ? `Recibido ${formatearMoneda(p.monto, p.moneda)}` : formatearMoneda(p.monto, p.moneda)}
              detalle={
                <>
                  {p.moneda === 'USD' && <span className="text-[12px] text-foreground-muted">{`TC ${p.tipoCambio.toFixed(2)} · ${formatearMoneda(p.montoMXN)}`}</span>}
                  {p.referencia && <span className="text-[12px] text-foreground-muted">Ref. {p.referencia}</span>}
                </>
              }
            />
          ))}
          {venta.cambioMXN > 0 && <p className="cifras text-right font-medium">{`Cambio ${formatearMoneda(venta.cambioMXN)}`}</p>}
        </Seccion>
      )}

      {(venta.propinas.length > 0 || venta.estado === 'pagada') && (
        <Seccion titulo="Propina">
          {venta.propinas.length === 0 ? (
            <p className="text-foreground-muted">Sin propina</p>
          ) : (
            venta.propinas.map((p, i) => (
              <Renglon
                key={i}
                concepto={p.barbero ? `Para ${p.barbero}` : 'Para repartir entre todos'}
                importe={`${formatearMoneda(p.monto, p.moneda)} en ${METODO[p.metodo].toLowerCase()}`}
              />
            ))
          )}
        </Seccion>
      )}

      {venta.devoluciones.length > 0 && (
        <Seccion titulo="Devoluciones">
          {venta.devoluciones.map((d, i) => (
            <Renglon
              key={i}
              concepto={
                <span className="flex flex-col">
                  <span>{d.motivo}</span>
                  <span className="cifras text-[12px] text-foreground-muted">{fechaHora(d.fecha)}</span>
                </span>
              }
              importe={`−${formatearMoneda(d.monto)}`}
              detalle={<Insignia tono={DEVOLUCION[d.estado].tono}>{DEVOLUCION[d.estado].texto}</Insignia>}
            />
          ))}
        </Seccion>
      )}
    </section>
  );
}
